import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { OrderSummary } from "./modalOrderSumary";
import { useSelector, useDispatch } from "react-redux";
import { getImageURL } from "../../../../api/image.api";
import { convertToBase64, resizeImage } from "../../../../functions/convertToBase64";
import { getLocalStorage } from "../../../../api/axios";
import { getUserMe } from "../../../../api/user.api";
import { State } from "../../../../reducer";
import { OrderSaucePicker, useOrderSauce } from "../../../../components/OrderSaucePicker";

const TYPES = [
  { value: "hot",    label: "Hot",    emoji: "🔥" },
  { value: "cold",   label: "Cold",   emoji: "🧊" },
  { value: "frappe", label: "Frappe", emoji: "🥤" },
];
const SWEETNESS = [
  { value: 0,   label: "0%" },
  { value: 25,  label: "25%" },
  { value: 50,  label: "50%" },
  { value: 75,  label: "75%" },
  { value: 100, label: "100%" },
];

export const FormOrder = () => {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const user = useSelector((state: State) => state.user);
  const order = useSelector((state: State) => state.order);
  const { menuName, menuID } = useParams();
  const menuIdAsNumber = menuID ? parseInt(menuID) : order.menu_id;
  const dispatch = useDispatch();

  const [tumblerType, setTumblerType] = useState<"" | "upload" | "recent">("");
  const [recentUpload, setRecentUpload] = useState("");
  const [savedTumbler, setSavedTumbler] = useState<string | null>(null);
  const [inputImage, setInputImage] = useState<string>("");
  const [inputValueAddition, setInputValueAddition] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedSweet, setSelectedSweet] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const { sauces, sauce, setSauce, valid: sauceValid, remark, previousNote } = useOrderSauce(
    menuIdAsNumber, order.menu_id === menuIdAsNumber ? order.remark : ""
  );

  useEffect(() => { if (previousNote) setInputValueAddition(previousNote); }, [previousNote]);

  // Pull the user's saved tumbler from /user/me — single source of truth, replaces localStorage.
  useEffect(() => {
    const t = getLocalStorage("access_token").now_token;
    if (!t) return;
    getUserMe({ access_token: t }).then((u) => {
      // @ts-ignore — backend now returns tumblerImage on user
      const url = u?.tumblerImage as string | undefined;
      if (url) setSavedTumbler(url);
    }).catch(() => {});
  }, []);

  // Prefill type / sweetness / remark when arriving via "Reorder last" — the
  // /statusOrder shortcut dispatches SETMENU with the previous order's payload
  // before navigating here, so we seed the form once on mount.
  useEffect(() => {
    if (!order || order.menu_id !== menuIdAsNumber) return;
    if (order.type) setSelectedType(order.type);
    if (order.sweetness) setSelectedSweet(order.sweetness);
    // Sauce and note are restored by useOrderSauce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tumbler prefill runs separately because it needs savedTumbler to land
  // first — we want to pick "recent" when the reorder's tumbler URL matches
  // the user's current saved tumbler, otherwise treat it as a one-off upload.
  useEffect(() => {
    if (!order?.tumblerImage || order.menu_id !== menuIdAsNumber) return;
    if (tumblerType) return; // user already chose, don't clobber
    if (savedTumbler && order.tumblerImage === savedTumbler) {
      setTumblerType("recent");
      setRecentUpload(savedTumbler);
    } else {
      setTumblerType("upload");
      setInputImage(order.tumblerImage);
    }
  }, [savedTumbler, order, menuIdAsNumber, tumblerType]);

  const isFormValid =
    Boolean(selectedType) && selectedSweet !== undefined && Boolean(inputImage || recentUpload) && sauceValid;

  const isAdmin = user?.role === "admin" || user?.role === "dev";
  const backTo = isAdmin ? "/menuCafe-admin" : "/menuCafe";

  const handleCancel = () => dispatch({ type: "CLEAR" });

  const handleInputImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    if (file.size / (1024 * 1024) > 5) return alert("File must be smaller than 5 MB");

    setIsLoading(true);
    try {
      const resizedFile = await resizeImage(file, 1);
      const base64String = await convertToBase64(resizedFile);
      const imageURL = await getImageURL({ file: base64String });
      if (imageURL) setInputImage(imageURL);
    } catch (e) {
      console.error("Error processing image:", e);
      // Show the real reason (e.g. "File too large (6.2 MB, max 5 MB)") —
      // a failed upload used to leave the form unchanged with no explanation.
      alert(e instanceof Error && e.message ? e.message : "Failed to process image. Please try another image.");
    }
    setIsLoading(false);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isFormValid) {
      alert("Please complete all required fields");
      return;
    }
    dispatch({
      type: "SETMENU",
      payload: {
        menu_id: menuIdAsNumber,
        menu_name: menuName,
        type: selectedType,
        sweetness: selectedSweet,
        remark: remark(inputValueAddition),
        tumblerImage: tumblerType === "upload" ? inputImage : recentUpload,
      },
    });
    setShowModal(true);
  };

  // legacy localStorage fallback (older clients) — DB-backed savedTumbler takes precedence
  const lsRecent = typeof window !== "undefined" ? localStorage.getItem("recentUpload") : null;
  const recentSrc = savedTumbler ?? lsRecent;

  return (
    <>
      <OrderSummary open={showModal} onClose={() => setShowModal(false)} />

      <div className={`min-h-screen bg-white font-sans ${showModal ? "hidden" : ""}`}>
        {/* === Top bar === */}
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-ink-100">
          <div className="px-3 h-[56px] flex items-center justify-between">
            <button
              type="button"
              onClick={() => { handleCancel(); navigate(backTo); }}
              aria-label="Back"
              className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-ink-1000 hover:bg-ink-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
            <p className="font-display font-bold text-base text-ink-1000 truncate flex-1 text-center px-2">
              Customize
            </p>
            <div className="w-[40px]" />
          </div>
        </header>

        <form onSubmit={handleSubmit} className="px-5 pt-[24px] pb-[120px] max-w-2xl mx-auto">
          {/* === Hero name === */}
          <div className="mb-[32px]">
            <p className="eyebrow-v2 mb-2">Selected drink</p>
            <h1 className="font-display font-black text-[36px] leading-[1.1] tracking-tightest text-ink-1000 break-words">
              {menuName}
            </h1>
          </div>

          {/* === Step 1: Type === */}
          <Section
            label="Step 1"
            title="Temperature"
            done={Boolean(selectedType)}
          >
            <div className="grid grid-cols-3 gap-2.5">
              {TYPES.map((t) => {
                const active = selectedType === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setSelectedType(t.value)}
                    className={`relative flex flex-col items-center justify-center gap-1.5 h-[88px] rounded-2xl border-2 transition-all active:scale-[0.97] ${
                      active
                        ? "border-ink-1000 bg-ink-50"
                        : "border-ink-100 bg-white hover:border-ink-300"
                    }`}
                  >
                    <span className="text-2xl">{t.emoji}</span>
                    <span className={`text-sm font-semibold ${active ? "text-ink-1000" : "text-ink-700"}`}>
                      {t.label}
                    </span>
                    {active && (
                      <div
                        className="absolute top-2 right-2 w-[18px] h-[18px] rounded-full text-white flex items-center justify-center"
                        style={{ background: "var(--accent)", color: "var(--accent-on)" }}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* === Step 2: Sweetness === */}
          <Section
            label="Step 2"
            title="Sweetness"
            done={selectedSweet !== undefined}
          >
            <div className="grid grid-cols-5 gap-2">
              {SWEETNESS.map((s) => {
                const active = selectedSweet === s.value;
                const isStandard = s.value === 100;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSelectedSweet(s.value)}
                    className={`relative h-[52px] rounded-2xl font-display font-bold text-sm transition-all active:scale-[0.97] border-2 ${
                      active
                        ? "border-accent bg-accent text-accent-on"
                        : "border-ink-100 bg-white text-ink-700 hover:border-ink-300"
                    }`}
                  >
                    {s.label}
                    {isStandard && (
                      <span className={`absolute -top-1.5 left-1/2 -translate-x-1/2 px-1.5 h-[16px] inline-flex items-center rounded-full text-[9px] font-bold uppercase tracking-wider ${
                        active ? "bg-white text-ink-1000" : "bg-ink-1000 text-white"
                      }`}>
                        Standard
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-ink-500">
              Standard sweetness is <span className="font-semibold text-ink-1000">100%</span>.
            </p>
          </Section>

          <OrderSaucePicker sauces={sauces} sauce={sauce} onChange={setSauce} />

          {/* === Step 3: Notes (optional) === */}
          <Section
            label="Step 3"
            title="Notes"
            optional
            done={Boolean(inputValueAddition)}
          >
            <div className="relative">
              <input
                type="text"
                value={inputValueAddition}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValueAddition(e.target.value)}
                maxLength={50}
                placeholder="e.g. less ice, no straw…"
                className="w-full h-[52px] px-4 rounded-2xl border border-ink-200 bg-ink-50 text-ink-1000 placeholder:text-ink-400 outline-none focus:border-ink-1000 focus:bg-white text-sm"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-400">
                {inputValueAddition.length}/50
              </span>
            </div>
          </Section>

          {/* === Step 4: Tumbler === */}
          <Section
            label="Step 4"
            title="Your tumbler"
            done={Boolean(inputImage || recentUpload)}
          >
            {!tumblerType && (
              <div className={`grid gap-2.5 ${recentSrc ? "grid-cols-1" : "grid-cols-1"}`}>
                {/* Recent tumbler — preview thumbnail card (preferred) */}
                {recentSrc && (
                  <button
                    type="button"
                    onClick={() => { setTumblerType("recent"); setRecentUpload(recentSrc); }}
                    className="flex items-center gap-3 p-3 rounded-2xl border-2 border-ink-1000 bg-ink-50 active:scale-[0.99] transition text-left"
                  >
                    <img
                      src={recentSrc}
                      alt="Your tumbler"
                      className="w-[72px] h-[72px] object-cover rounded-xl flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-base text-ink-1000">Use your tumbler</p>
                      <p className="text-xs text-ink-500 mt-0.5">Tap to reuse this photo</p>
                    </div>
                    <svg className="w-5 h-5 text-ink-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                )}

                {/* Upload — full card if first, smaller if recent exists */}
                <button
                  type="button"
                  onClick={() => { setTumblerType("upload"); setRecentUpload(""); setInputImage(""); }}
                  className={`flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ink-200 hover:border-ink-1000 active:scale-[0.97] transition ${
                    recentSrc ? "h-[56px]" : "h-[112px] flex-col"
                  }`}
                >
                  <svg className="w-5 h-5 text-ink-700" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
                  </svg>
                  <span className="text-sm font-semibold text-ink-1000">
                    {recentSrc ? "Upload a different one" : "Upload tumbler photo"}
                  </span>
                </button>
              </div>
            )}

            {tumblerType && (
              <div>
                <button
                  type="button"
                  onClick={() => { setTumblerType(""); setInputImage(""); setRecentUpload(""); }}
                  className="text-xs text-ink-500 hover:text-ink-1000 mb-3 flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
                  </svg>
                  Change
                </button>

                {tumblerType === "upload" && !inputImage && (
                  <label
                    htmlFor="tumbler_file"
                    className="block w-full h-[160px] rounded-2xl border-2 border-dashed border-ink-300 bg-ink-50 hover:border-ink-1000 cursor-pointer flex flex-col items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <svg className="w-6 h-6 animate-spin text-ink-700" fill="none" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
                          <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                        <span className="text-sm text-ink-700">Uploading…</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-7 h-7 text-ink-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"/>
                        </svg>
                        <span className="text-sm font-semibold text-ink-1000">Tap to choose photo</span>
                        <span className="text-xs text-ink-400">JPEG/PNG/HEIC, ≤5 MB</span>
                      </>
                    )}
                    <input
                      id="tumbler_file"
                      type="file"
                      accept=".heic,.heif,.png,.jpeg,.jpg"
                      onChange={handleInputImage}
                      className="hidden"
                    />
                  </label>
                )}

                {(inputImage || recentUpload) && (
                  <div className="relative w-[180px] mx-auto">
                    <img
                      src={inputImage || recentUpload}
                      alt={menuName}
                      className="w-full aspect-square object-cover rounded-2xl"
                    />
                  </div>
                )}
              </div>
            )}
          </Section>
        </form>

        {/* === Sticky CTA === */}
        <div className="fixed bottom-0 left-0 right-0 md:left-[280px] z-20 bg-white/95 backdrop-blur-md border-t border-ink-100 px-5 py-3">
          <div className="max-w-2xl mx-auto">
            <button
              type="button"
              onClick={handleSubmit as unknown as React.MouseEventHandler<HTMLButtonElement>}
              disabled={!isFormValid || isLoading}
              className="btn-v2-primary btn-v2-lg w-full"
            >
              {isFormValid ? "Add to order" : "Complete steps to continue"}
              {isFormValid && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const Section: React.FC<{
  label: string;
  title: string;
  done?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}> = ({ label, title, done, optional, children }) => (
  <section className="mb-[32px]">
    <div className="flex items-baseline justify-between mb-3">
      <div className="flex items-baseline gap-2">
        <span className="eyebrow-v2">{label}</span>
        <h2 className="font-display font-black text-[18px] tracking-tight text-ink-1000">{title}</h2>
      </div>
      {optional ? (
        <span className="text-xs text-ink-400">Optional</span>
      ) : done ? (
        <span className="text-xs font-semibold text-ink-1000 inline-flex items-center gap-1">
          <span
            className="inline-flex w-[14px] h-[14px] rounded-full items-center justify-center"
            style={{ background: "var(--accent)", color: "var(--accent-on)" }}
          >
            <svg className="w-2 h-2" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
          </span>
          Done
        </span>
      ) : (
        <span className="text-xs text-red-500 font-medium">Required</span>
      )}
    </div>
    {children}
  </section>
);
